import type {
  CleFieldValidationResult,
  FieldValidationMap,
} from "../models/fieldValidation";
import {
  getFieldValidationLabel,
  normalizeFieldKey,
  resolveFieldValidationReason,
} from "./fieldValidationMessages";

const readString = (value: unknown): string | null =>
  typeof value === "string" && value.trim().length > 0 ? value : null;

const readFieldValidationEntry = (
  value: unknown,
): CleFieldValidationResult | null => {
  if (!value || typeof value !== "object") {
    return null;
  }

  const entry = value as Record<string, unknown>;

  if (typeof entry.approved !== "boolean") {
    return null;
  }

  return {
    approved: entry.approved,
    reason: readString(entry.reason),
  };
};

export const readFieldValidationFromApi = (
  value: unknown,
): FieldValidationMap | null => {
  if (!value || typeof value !== "object") {
    return null;
  }

  const result: FieldValidationMap = {};

  for (const [key, entry] of Object.entries(value)) {
    const parsedEntry = readFieldValidationEntry(entry);

    if (parsedEntry) {
      result[key] = parsedEntry;
    }
  }

  return Object.keys(result).length > 0 ? result : null;
};

const toPascalCaseKey = (key: string): string =>
  key.length > 0 ? `${key[0].toUpperCase()}${key.slice(1)}` : key;

const lookupFieldValidation = (
  fieldValidation: FieldValidationMap | null | undefined,
  key: string,
): CleFieldValidationResult | null =>
  fieldValidation?.[key] ?? fieldValidation?.[toPascalCaseKey(key)] ?? null;

export type FieldValidationItemState = {
  isValid?: boolean;
  showValidationState?: boolean;
  reason?: string;
};

export const getFieldValidationItem = (
  fieldValidation: FieldValidationMap | null | undefined,
  key: string,
): FieldValidationItemState => {
  const result = lookupFieldValidation(fieldValidation, key);

  if (!result) {
    return { showValidationState: false };
  }

  if (result.approved) {
    return {
      isValid: true,
      showValidationState: true,
    };
  }

  return {
    isValid: false,
    showValidationState: true,
    reason: resolveFieldValidationReason(key, result.reason),
  };
};

/**
 * Rows for failing fields the caller did not build itself.
 *
 * The API enumerates its rules by hand and each view enumerates its rows by
 * hand; nothing reconciles the two, so a rule the UI has no row for rejects the
 * document invisibly (this is what happened with `courseTime`). Appending the
 * leftovers means a new backend rule can never go unseen again.
 *
 * Rows are inserted before the last existing row, because the validation grid
 * gives its final row full width for long text such as "Kursusbeskrivelse".
 */
/**
 * True when the API returned per-field results and every one of them passes.
 *
 * A document can sit in `rejected_by_ai` while this is true: `fieldValidation`
 * is recomputed from the current editable values on every read, but nothing
 * recomputes `status` when the secretariat corrects a field. Callers use this to
 * avoid telling the member a requirement is failing when none is.
 */
export const allFieldsApproved = (
  fieldValidation: FieldValidationMap | null | undefined,
): boolean => {
  if (!fieldValidation) {
    return false;
  }

  const results = Object.values(fieldValidation);

  return results.length > 0 && results.every((result) => result.approved);
};

export const appendUnmatchedFailingFields = <T extends { label: string }>(
  rows: T[],
  fieldValidation: FieldValidationMap | null | undefined,
  consumedKeys: readonly string[],
  createRow: (label: string, reason: string) => T,
): T[] => {
  if (!fieldValidation) {
    return rows;
  }

  const consumed = new Set(consumedKeys.map(normalizeFieldKey));
  // The card keys rows by label, so a collision would break its expand state.
  const usedLabels = new Set(rows.map((row) => row.label));

  const extraRows: T[] = [];

  for (const [key, result] of Object.entries(fieldValidation)) {
    if (result.approved || consumed.has(normalizeFieldKey(key))) {
      continue;
    }

    const label = getFieldValidationLabel(normalizeFieldKey(key));

    if (usedLabels.has(label)) {
      continue;
    }

    usedLabels.add(label);
    extraRows.push(
      createRow(label, resolveFieldValidationReason(key, result.reason)),
    );
  }

  if (extraRows.length === 0) {
    return rows;
  }

  return [...rows.slice(0, -1), ...extraRows, ...rows.slice(-1)];
};
